/**
 * CR-7460: what a document upload accepts, and how a refusal is worded.
 *
 * A file picker must offer exactly what its endpoint's server-side allow-list
 * accepts, or the user picks a file the server then refuses. Each list here is
 * the single frontend copy of one server allow-list — pickers build their
 * `accept` attribute from it with {@link acceptAttr} rather than repeating
 * literal strings.
 *
 * {@link describeFileTypes} / {@link fileTypeRefusedMessage} mirror
 * `backend/core/upload_types.py`, so a file refused in the browser and one
 * refused by the server read the same: "… PDF, Word, Excel, PNG or JPEG files
 * only."
 */

/** Document manager — documents.Document.ALLOWED_EXTENSIONS (no SVG: CWE-79). */
export const DOCUMENT_MANAGER_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'xlsb', 'ppt', 'pptx', 'txt', 'csv',
  'zip', 'rar', '7z', 'jpg', 'jpeg', 'png', 'gif', 'bmp',
] as const;

/** Plain-words label per extension, in the order labels are listed. */
const EXTENSION_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['pdf', 'PDF'],
  ['doc', 'Word'], ['docx', 'Word'],
  ['xls', 'Excel'], ['xlsx', 'Excel'], ['xlsb', 'Excel'],
  ['ppt', 'PowerPoint'], ['pptx', 'PowerPoint'],
  ['txt', 'TXT'], ['csv', 'CSV'],
  ['zip', 'ZIP'], ['rar', 'RAR'], ['7z', '7Z'],
  ['png', 'PNG'], ['jpg', 'JPEG'], ['jpeg', 'JPEG'], ['gif', 'GIF'],
  ['bmp', 'BMP'], ['svg', 'SVG'], ['webp', 'WebP'],
];

const LABEL_BY_EXTENSION: Record<string, string> = Object.fromEntries(EXTENSION_LABELS);
const LABEL_ORDER: Record<string, number> = Object.fromEntries(
  EXTENSION_LABELS.map(([ext], index) => [ext, index]),
);

/** MIME type → extension, for `accept` tokens written as MIME types. */
const MIME_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
};

/** `pdf` / `.PDF` / `application/pdf` → `pdf`; a family such as `image/*` is kept as is. */
function toExtension(fileType: string): string {
  const value = fileType.trim().toLowerCase();
  if (value.endsWith('/*')) return value;
  if (value.includes('/')) return MIME_EXTENSIONS[value] ?? value.split('/').pop() ?? value;
  return value.replace(/^\./, '');
}

/** Label for one normalised type: "Word", or "image" for `image/*`. */
function labelFor(ext: string): string {
  if (ext.endsWith('/*')) return ext.slice(0, -2);
  return LABEL_BY_EXTENSION[ext] ?? ext.toUpperCase();
}

/**
 * The accepted types of an allow-list, in plain words.
 *
 * @param fileTypes - Extensions (`pdf` / `.pdf`) and/or MIME types
 * @returns e.g. "PDF, Word, Excel, PNG or JPEG"
 * @example describeFileTypes(['.pdf', '.docx', '.png']) // "PDF, Word or PNG"
 */
export function describeFileTypes(fileTypes: readonly string[]): string {
  const unknownRank = EXTENSION_LABELS.length;
  const extensions = Array.from(new Set(fileTypes.filter(Boolean).map(toExtension))).sort(
    (a, b) => (LABEL_ORDER[a] ?? unknownRank) - (LABEL_ORDER[b] ?? unknownRank) || a.localeCompare(b),
  );
  const labels: string[] = [];
  extensions.forEach((ext) => {
    const label = labelFor(ext);
    if (!labels.includes(label)) labels.push(label);
  });
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

/**
 * The message for a file refused because of its type — same wording as the
 * server's refusal.
 *
 * @param fileTypes - The upload's allow-list
 * @returns e.g. "This file type is not accepted. Upload PDF or PNG files only."
 */
export function fileTypeRefusedMessage(fileTypes: readonly string[]): string {
  return `This file type is not accepted. Upload ${describeFileTypes(fileTypes)} files only.`;
}

/**
 * The `accept` attribute for an allow-list of extensions.
 *
 * @param extensions - Bare extensions, e.g. PO_DOCUMENT_EXTENSIONS
 * @returns e.g. ".pdf,.doc,.docx"
 */
export function acceptAttr(extensions: readonly string[]): string {
  return extensions.map((ext) => `.${ext}`).join(',');
}

/**
 * Whether a file name carries one of the allowed extensions.
 *
 * @param fileName - The picked file's name
 * @param extensions - Bare extensions
 * @returns True when the server would accept the type
 */
export function hasAllowedExtension(fileName: string, extensions: readonly string[]): boolean {
  const ext = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : undefined;
  return !!ext && extensions.includes(ext);
}
